'use client'

import type { CrossSellingServiceResponse } from 'cross-selling/types'
import {
	type Dispatch,
	type SetStateAction,
	createContext,
	useContext,
	useMemo,
	useState,
} from 'react'

export type ShopTheLookContextType = {
	isOpen: boolean
	setIsOpen: Dispatch<SetStateAction<boolean>>
	products: CrossSellingServiceResponse[]
	setProducts: Dispatch<SetStateAction<CrossSellingServiceResponse[]>>
}

type ShopTheLookProviderProps = {
	children: React.ReactNode
}

export const ShopTheLookContext = createContext<
	ShopTheLookContextType | undefined
>(undefined)

export function ShopTheLookProvider({
	children,
}: ShopTheLookProviderProps): React.ReactNode {
	const [isOpen, setIsOpen] = useState(false)
	const [products, setProducts] = useState<CrossSellingServiceResponse[]>([])

	const value = useMemo(
		() => ({
			isOpen,
			setIsOpen,
			products,
			setProducts,
		}),
		[isOpen, setIsOpen, products, setProducts]
	)

	return (
		<ShopTheLookContext.Provider value={value}>
			{children}
		</ShopTheLookContext.Provider>
	)
}

/** @throws {Error} If useShopTheLook is used without ShopTheLookProvider */
export const useShopTheLook = (): ShopTheLookContextType => {
	const context = useContext(ShopTheLookContext)
	if (!context) {
		throw new Error('useShopTheLook must be used within a ShopTheLookProvider')
	}
	return context
}
